import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, Package } from "lucide-react";

const EMPTY_FORM = {
    id: 0,
    nome: "",
    descrizione: "",
    icona: "Package",
    link_url: "",
    button_text: "Apri prodotto",
    sort_order: 0,
    is_active: true,
};

const iconOptions = [
    "Package",
    "Tag",
    "Thermometer",
    "Probe",
    "PlugZap",
    "Cable",
    "Router",
    "Wifi",
];

export default function AdminShop() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    async function load() {
        try {
            setLoading(true);
            setErr("");
            setMsg("");

            const res = await fetch("/api/admin_shop_list.php", {
                credentials: "include",
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.error || "Errore caricamento prodotti");
            }

            setItems(data.items || []);
        } catch (e) {
            setErr(e.message || "Errore");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    function editItem(item) {
        setForm({
            id: Number(item.id || 0),
            nome: item.nome || "",
            descrizione: item.descrizione || "",
            icona: item.icona || "Package",
            link_url: item.link_url || "",
            button_text: item.button_text || "Apri prodotto",
            sort_order: Number(item.sort_order || 0),
            is_active: Number(item.is_active) === 1 || item.is_active === true,
        });
        setErr("");
        setMsg("");
    }

    function resetForm() {
        setForm(EMPTY_FORM);
    }

    async function saveItem(e) {
        e.preventDefault();
        try {
            setSaving(true);
            setErr("");
            setMsg("");

            const res = await fetch("/api/admin_shop_save.php", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.error || "Errore salvataggio");
            }

            setMsg(form.id ? "Prodotto aggiornato." : "Prodotto creato.");
            setForm(EMPTY_FORM);
            await load();
        } catch (e) {
            setErr(e.message || "Errore");
        } finally {
            setSaving(false);
        }
    }

    async function deleteItem(id) {
        if (!window.confirm("Eliminare questo prodotto?")) return;

        try {
            setErr("");
            setMsg("");

            const res = await fetch("/api/admin_shop_delete.php", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.error || "Errore eliminazione");
            }

            setMsg("Prodotto eliminato.");
            if (Number(form.id) === Number(id)) setForm(EMPTY_FORM);
            await load();
        } catch (e) {
            setErr(e.message || "Errore");
        }
    }

    return (
        <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                    <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                        <Package size={22} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-slate-900">Gestione Shop</h2>
                        <p className="text-sm text-slate-600">
                            Crea e modifica i prodotti visibili ai clienti.
                        </p>
                    </div>
                </div>

                {msg && (
                    <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                        {msg}
                    </div>
                )}

                {err && (
                    <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {err}
                    </div>
                )}

                <form onSubmit={saveItem} className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">Nome prodotto</label>
                        <input
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.nome}
                            onChange={(e) => setForm({ ...form, nome: e.target.value })}
                            required
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">Descrizione</label>
                        <textarea
                            className="min-h-[90px] w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.descrizione}
                            onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Icona</label>
                        <select
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.icona}
                            onChange={(e) => setForm({ ...form, icona: e.target.value })}
                        >
                            {iconOptions.map((icon) => (
                                <option key={icon} value={icon}>
                                    {icon}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Testo bottone</label>
                        <input
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.button_text}
                            onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">Link prodotto</label>
                        <input
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.link_url}
                            onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                            placeholder="https://..."
                            required
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Ordine</label>
                        <input
                            type="number"
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-slate-500"
                            value={form.sort_order}
                            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value || 0) })}
                        />
                    </div>

                    <div className="flex items-center gap-3 pt-8">
                        <input
                            id="is_active"
                            type="checkbox"
                            checked={!!form.is_active}
                            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                            Prodotto attivo
                        </label>
                    </div>

                    <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                        >
                            {form.id ? <Save size={16} /> : <Plus size={16} />}
                            {saving ? "Salvataggio..." : form.id ? "Aggiorna prodotto" : "Aggiungi prodotto"}
                        </button>

                        <button
                            type="button"
                            onClick={resetForm}
                            className="rounded-2xl border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Nuovo
                        </button>
                    </div>
                </form>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-slate-900">Prodotti esistenti</h3>

                {loading ? (
                    <div className="text-slate-600">Caricamento...</div>
                ) : items.length === 0 ? (
                    <div className="text-slate-600">Nessun prodotto presente.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-slate-500">
                                    <th className="px-3 py-3">Ordine</th>
                                    <th className="px-3 py-3">Nome</th>
                                    <th className="px-3 py-3">Icona</th>
                                    <th className="px-3 py-3">Stato</th>
                                    <th className="px-3 py-3">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id} className="border-b border-slate-100">
                                        <td className="px-3 py-3">{item.sort_order}</td>
                                        <td className="px-3 py-3 font-medium text-slate-800">{item.nome}</td>
                                        <td className="px-3 py-3 text-slate-600">{item.icona}</td>
                                        <td className="px-3 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${Number(item.is_active) === 1
                                                        ? "bg-emerald-50 text-emerald-700"
                                                        : "bg-slate-100 text-slate-500"
                                                    }`}
                                            >
                                                {Number(item.is_active) === 1 ? "Attivo" : "Disattivo"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => editItem(item)}
                                                    className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50"
                                                >
                                                    <Pencil size={14} />
                                                    Modifica
                                                </button>
                                                <button
                                                    onClick={() => deleteItem(item.id)}
                                                    className="inline-flex items-center gap-1 rounded-xl border border-rose-300 px-3 py-2 text-rose-700 hover:bg-rose-50"
                                                >
                                                    <Trash2 size={14} />
                                                    Elimina
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}